import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from '../../firebase/config';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { Helmet } from 'react-helmet-async';

export default function SellerProfile() {
  const { id } = useParams();
  const [seller, setSeller] = useState(null);
  const [activeListings, setActiveListings] = useState([]);
  const [soldListings, setSoldListings] = useState([]);
  const [reviews, setReviews] = useState([]); 
  const [activeTab, setActiveTab] = useState('active'); // 'active', 'sold', 'reviews'
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSellerData = async () => {
      try {
        // 1. Get Seller Info
        const sellerDoc = await getDoc(doc(db, 'users', id));
        if (sellerDoc.exists()) setSeller({ id: sellerDoc.id, ...sellerDoc.data() });

        // 2. Get Seller's Listings (Active & Sold)
        const listQuery = query(collection(db, 'listings'), where('sellerId', '==', id));
        const listSnap = await getDocs(listQuery);
        
        const active = [];
        const sold = [];
        listSnap.forEach(doc => {
          const item = { id: doc.id, ...doc.data() };
          if (item.status === 'active') active.push(item);
          if (item.status === 'sold') sold.push(item);
        });
        
        setActiveListings(active);
        setSoldListings(sold);

        // 3. Get Seller's Reviews
        const revQuery = query(collection(db, 'reviews'), where('sellerId', '==', id));
        const revSnap = await getDocs(revQuery);
        setReviews(revSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      } catch (error) {
        console.error("Error fetching seller:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSellerData();
  }, [id]);

  // 🚨 OPTIMIZATION: Pulls the fast 600x600 thumbnail to save bandwidth
  const getThumbnail = (item) => {
    let url = item.mainImage || item.imageUrls?.[0] || item.imageUrl;
    if (!url) return 'https://via.placeholder.com/400x300?text=No+Image+Available';
    if (url.includes('firebasestorage.googleapis.com') && !url.includes('_600x600')) {
      url = url.replace('.jpg?alt=media', '_600x600.jpg?alt=media')
               .replace('.jpeg?alt=media', '_600x600.jpeg?alt=media')
               .replace('.png?alt=media', '_600x600.png?alt=media')
               .replace('.webp?alt=media', '_600x600.webp?alt=media');
    }
    return url;
  };

  if (loading) return <div style={{ padding: '100px', textAlign: 'center', color: '#64748b', fontWeight: '600' }}>Loading Storefront...</div>;
  if (!seller) return <div style={{ padding: '100px', textAlign: 'center', color: '#0f172a', fontWeight: 'bold' }}>Seller not found.</div>;

  // 🚨 FIXED: Safe Reputation Math
  const totalReviews = reviews.length;
  const averageRating = totalReviews > 0 
    ? (reviews.reduce((sum, review) => sum + Number(review.rating || 0), 0) / totalReviews).toFixed(1)
    : null;

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const emptyStars = 5 - fullStars;
    return '⭐'.repeat(fullStars) + '☆'.repeat(emptyStars);
  };

  // --- GAMIFIED BADGE STYLING MAP ---
  const badgeStyles = {
    'Fast Shipper': { icon: '🚀', bg: '#d1ecf1', text: '#0c5460', border: '#bee5eb' },
    'Local Legend': { icon: '🤝', bg: '#d4edda', text: '#155724', border: '#c3e6cb' },
    'Hardware Guru': { icon: '💻', bg: '#e2d9f3', text: '#6f42c1', border: '#d6c8ec' }
  };

  return (
    <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh', padding: '40px 20px', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      
      <Helmet>
        <title>{seller.email?.split('@')[0]}'s Storefront | Nundinaria</title>
        <meta name="description" content={`Shop gear from ${seller.email?.split('@')[0]} on Nundinaria.`} />
      </Helmet>

      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        
        {/* --- SELLER HEADER WITH TRUST ENGINE --- */}
        <div style={{ backgroundColor: 'white', padding: '40px', borderRadius: '24px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', border: '1px solid #e2e8f0', marginBottom: '40px', display: 'flex', alignItems: 'center', gap: '30px', flexWrap: 'wrap' }}>
          
          <div style={{ width: '100px', height: '100px', backgroundColor: '#4f46e5', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '40px', fontWeight: 'bold', flexShrink: 0, boxShadow: '0 10px 15px -3px rgba(79, 70, 229, 0.3)' }}>
            {seller.email?.charAt(0).toUpperCase()}
          </div>
          
          <div style={{ flex: 1 }}>
            <h1 style={{ margin: '0 0 10px 0', fontSize: '32px', color: '#0f172a', fontWeight: '800', letterSpacing: '-0.5px' }}>
              {seller.email?.split('@')[0]}'s Storefront
            </h1>
            <p style={{ margin: '0 0 5px 0', color: '#475569', fontSize: '15px' }}>📍 <strong>Location:</strong> {seller.location || 'United States'}</p>
            {/* 🚨 FIXED: Safe Timestamp Rendering */}
            <p style={{ margin: 0, color: '#475569', fontSize: '15px' }}>
              📅 <strong>Member Since:</strong> {seller.joinedAt?.toDate ? seller.joinedAt.toDate().toLocaleDateString() : (seller.joinedAt ? new Date(seller.joinedAt).toLocaleDateString() : 'Recently')}
            </p>
            
            {/* STATS & VERIFICATION */}
            <div style={{ marginTop: '16px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <span style={{ backgroundColor: '#e0e7ff', color: '#4f46e5', padding: '6px 12px', borderRadius: '50px', fontSize: '13px', fontWeight: '700' }}>✓ Verified Member</span>
              <span style={{ backgroundColor: '#f1f5f9', color: '#475569', padding: '6px 12px', borderRadius: '50px', fontSize: '13px', fontWeight: '700', border: '1px solid #cbd5e1' }}>{activeListings.length} Active Items</span>
            </div>

            {/* GAMIFICATION BADGES */}
            {seller.badges && seller.badges.length > 0 && (
              <div style={{ marginTop: '12px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {seller.badges.map(badgeName => {
                  const style = badgeStyles[badgeName] || { icon: '🏅', bg: '#fff3cd', text: '#856404', border: '#ffeeba' };
                  return (
                    <span key={badgeName} style={{ backgroundColor: style.bg, color: style.text, border: `1px solid ${style.border}`, padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {style.icon} {badgeName}
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* REPUTATION BADGE */}
          <div style={{ backgroundColor: '#f8fafc', padding: '20px', borderRadius: '16px', border: '1px solid #cbd5e1', textAlign: 'center', minWidth: '160px' }}>
            <p style={{ margin: '0 0 5px 0', fontSize: '12px', color: '#64748b', textTransform: 'uppercase', fontWeight: '800', letterSpacing: '1px' }}>Reputation</p>
            {averageRating ? (
              <>
                <div style={{ fontSize: '28px', fontWeight: '900', color: '#0f172a', margin: '5px 0' }}>
                  {averageRating} <span style={{ fontSize: '18px', color: '#fbbf24' }}>{renderStars(averageRating)}</span>
                </div>
                <p style={{ margin: 0, fontSize: '13px', color: '#4f46e5', fontWeight: '700' }}>Based on {totalReviews} reviews</p>
              </>
            ) : (
              <div style={{ padding: '10px 0' }}>
                <span style={{ backgroundColor: '#fffbeb', color: '#b45309', padding: '6px 12px', borderRadius: '6px', fontSize: '13px', fontWeight: '800', border: '1px solid #fde68a' }}>New Seller</span>
              </div>
            )}
          </div>
          
        </div>

        {/* --- NAVIGATION TABS --- */}
        <div style={{ display: 'flex', gap: '24px', borderBottom: '2px solid #e2e8f0', marginBottom: '32px', overflowX: 'auto' }}>
          <button onClick={() => setActiveTab('active')} style={tabStyle(activeTab === 'active')}>
            Active Gear ({activeListings.length})
          </button>
          <button onClick={() => setActiveTab('sold')} style={tabStyle(activeTab === 'sold')}>
            Past Sales ({soldListings.length})
          </button>
          <button onClick={() => setActiveTab('reviews')} style={tabStyle(activeTab === 'reviews')}>
            Reviews ({reviews.length})
          </button>
        </div>

        {/* --- TAB CONTENT --- */}
        <div style={{ minHeight: '400px' }}>
          
          {/* ACTIVE OR SOLD GEAR GRID */}
          {(activeTab === 'active' || activeTab === 'sold') && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '24px' }}>
              {(activeTab === 'active' ? activeListings : soldListings).map((item) => (
                <Link to={`/item/${item.id}`} key={item.id} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div style={{ backgroundColor: 'white', borderRadius: '20px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', height: '100%', display: 'flex', flexDirection: 'column', transition: 'transform 0.2s, box-shadow 0.2s', opacity: activeTab === 'sold' ? 0.7 : 1 }}
                    onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.1)'; }}
                    onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.05)'; }}
                  >
                    <div style={{ height: '220px', backgroundColor: '#f1f5f9', position: 'relative' }}>
                      {/* Fallback Waterfall included */}
                      <img 
                        src={getThumbnail(item)} 
                        alt={item.title} 
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                        onError={(e) => { 
                          if (e.target.src.includes('_600x600')) {
                            e.target.src = e.target.src.replace('_600x600', '');
                          } else {
                            e.target.onerror = null; 
                            e.target.src = 'https://via.placeholder.com/400x300?text=Image+Unavailable'; 
                          }
                        }}
                      />
                      {activeTab === 'sold' && (
                        <div style={{ position: 'absolute', top: '12px', right: '12px', background: '#0f172a', color: 'white', padding: '6px 14px', borderRadius: '8px', fontWeight: '800', fontSize: '11px', letterSpacing: '0.5px' }}>
                          SOLD
                        </div>
                      )}
                    </div>
                    <div style={{ padding: '20px', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#0f172a', fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.title}</h3>
                      <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                        <span style={{ fontSize: '22px', fontWeight: '800', color: '#0f172a', letterSpacing: '-0.5px' }}>${Number(item.price).toFixed(2)}</span>
                        <span style={{ fontSize: '11px', color: '#475569', backgroundColor: '#f1f5f9', padding: '4px 8px', borderRadius: '6px', fontWeight: '600' }}>{item.condition}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
              {(activeTab === 'active' ? activeListings : soldListings).length === 0 && (
                <div style={{ gridColumn: '1 / -1', padding: '60px 20px', textAlign: 'center', backgroundColor: 'white', borderRadius: '20px', border: '1px dashed #cbd5e1' }}>
                  <p style={{ color: '#64748b', fontSize: '16px', margin: 0 }}>This seller has no {activeTab} items right now.</p>
                </div>
              )}
            </div>
          )}

          {/* REVIEWS LIST */}
          {activeTab === 'reviews' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {reviews.map(review => (
                <div key={review.id} style={{ backgroundColor: 'white', padding: '24px', borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <span style={{ fontWeight: '800', color: '#0f172a', fontSize: '15px' }}>{review.buyerName || 'Verified Buyer'}</span>
                    <span style={{ color: '#f59e0b', fontSize: '18px', letterSpacing: '2px' }}>{renderStars(review.rating)}</span>
                  </div>
                  <p style={{ margin: 0, color: '#475569', lineHeight: '1.6', fontSize: '15px' }}>"{review.text}"</p>
                  <p style={{ margin: '12px 0 0 0', fontSize: '12px', color: '#94a3b8', fontWeight: '600' }}>Purchased: {review.itemTitle}</p>
                </div>
              ))}
              {reviews.length === 0 && (
                <div style={{ padding: '60px 20px', textAlign: 'center', backgroundColor: 'white', borderRadius: '20px', border: '1px dashed #cbd5e1' }}>
                  <p style={{ color: '#64748b', fontSize: '16px', margin: 0 }}>No reviews have been posted yet.</p>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

// Reusable Tab Styling
const tabStyle = (isActive) => ({
  padding: '12px 16px',
  background: 'none',
  border: 'none',
  borderBottom: isActive ? '3px solid #4f46e5' : '3px solid transparent',
  color: isActive ? '#0f172a' : '#64748b',
  fontWeight: isActive ? '800' : '600',
  fontSize: '15px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  whiteSpace: 'nowrap',
  outline: 'none'
});